'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { m, useInView } from 'motion/react';
import { useRef, useState } from 'react';
import { useCopyToClipboard } from 'react-use';

import { Mission } from '@flipster/apis/v1/campaign/types';
import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import { NavigationHeightVariableStyleValue } from '@flipster/react/panda/globals/vars';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { convertWebviewToWebUrl } from '@flipster/utils/env-helper';
import segment from '@flipster/utils/segment';
import { CampaignEvent } from '@flipster/utils/segment/events/campaign';

import { CustomLink } from '../../../misc/link-for-webview';
import { useReferralCode } from '../../../shared/referral-code/state';
import { ImgOrLottie } from '../../assets';
import { missionsBunja, type MissionTask } from './state';

const Choice = createSingleChoice<string>();

export const Missions = () => {
  const { slug, eventLocation, groups, missionsAtom, showUpdatedTime, hasMultipleGroups } = useBunja(missionsBunja);
  const missions = useAtomValue(missionsAtom);

  const [activeIndex, setActiveIndex] = useState<string>('0');
  const index = Number(activeIndex);
  const currentGroup = groups[index];

  if (!slug || !currentGroup || groups.length === 0) return null;

  return (
    <div id='missions' className={css({ my: 40 })} style={{ scrollMarginTop: NavigationHeightVariableStyleValue }}>
      <h3
        className={hstack({
          textStyle: 'Title/M',
          color: 'var(--key-color, {colors.content.accent.promotion})',
          mb: 16,
          gap: 8,
        })}
      >
        {currentGroup.title}
        {currentGroup.icon?.url && (
          <ImgOrLottie
            url={currentGroup.icon.url}
            alt='mission-icon'
            css={{ w: { base: 30, tablet: 44 }, h: { base: 30, tablet: 44 } }}
          />
        )}
      </h3>

      {hasMultipleGroups && (
        <div
          className={stack({
            justify: 'space-between',
            flexDirection: { base: 'column', tablet: 'row' },
            alignItems: { tablet: 'center' },
            gap: 12,
            mb: { base: 12, tablet: 32 },
            overflowX: 'scroll',
          })}
        >
          <Choice value={activeIndex} onChange={setActiveIndex}>
            {groups.map((group, i) => (
              <Choice.Chip key={group.id} value={i.toString()} label={group.title ?? ''} />
            ))}
          </Choice>
          {showUpdatedTime && (
            <p
              className={css({
                textStyle: 'Text/M/Regular',
                color: 'content.default.level-3',
                textAlign: 'left',
              })}
            >
              <FormattedMessage id='Campaign.r251124.Mission_status_update_minutes' values={{ minute: 5 }} />
            </p>
          )}
        </div>
      )}

      {!hasMultipleGroups && showUpdatedTime && (
        <p
          className={css({
            textStyle: 'Text/M/Regular',
            color: 'content.default.level-3',
            textAlign: 'left',
            mb: 16,
          })}
        >
          <FormattedMessage id='Campaign.r251124.Mission_status_update_minutes' values={{ minute: 5 }} />
        </p>
      )}

      <ul className={stack({ gap: 8 })}>
        {currentGroup.tasks.map((task, i) => {
          if (!task.id) return null;
          const mission = missions?.[task.id];
          return (
            <MissionItem
              key={task.id}
              task={task}
              mission={mission ?? null}
              listOrder={i}
              slug={slug}
              eventLocation={eventLocation ?? ''}
            />
          );
        })}
      </ul>
    </div>
  );
};

interface MissionItemProps {
  task: MissionTask;
  mission: Mission | null;
  listOrder: number;
  slug: string;
  eventLocation: string;
}

const MissionItem = ({ task, mission, listOrder, slug, eventLocation }: MissionItemProps) => {
  return (
    <li
      className={stack({
        justify: 'center',
        p: 12,
        rounded: 8,
        bg: 'background.default.level-1',
        textAlign: 'left',
      })}
    >
      <div className={hstack({ justifyContent: 'space-between', alignItems: 'flex-end' })}>
        <div>
          <p
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'inverse.white',
            })}
          >
            {task.title}
          </p>
          <div
            className={flex({
              flexDirection: { base: 'column', tablet: 'row' },
              alignItems: { base: 'start', tablet: 'center' },
              gap: 2,
              mt: 2,
            })}
          >
            <span
              className={css({
                textStyle: 'Text/L/Strong',
                color: 'content.default.level-5',
              })}
            >
              {task.description}
            </span>
            {task.badge && (
              <Badge type='tint' color='promotion'>
                {task.badge}
              </Badge>
            )}
          </div>
        </div>
        {mission?.achieved && <CheckCircleIcon size={30} color={token('colors.content.accent.info')} />}
        {!mission?.achieved && task.button && (
          <MissionButton
            json={task.button}
            logger={() => {
              segment.track(CampaignEvent.MissionTaskListSelectClick, {
                event_location: eventLocation,
                slug,
                list_order: listOrder,
                mission_id: task.id ?? 'unknown',
              });
            }}
          />
        )}
      </div>
      {mission?.progress && (
        <div className={css({ mt: 16 })}>
          <AnimatedProgressBar progress={mission.progress} />
          <div
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-1',
              mt: 4,
            })}
          >
            <Formatted.Number
              value={mission.progress.currentValue}
              css={{ color: 'content.accent.promotion' }}
              precision={0}
            />
            {' / '}
            <Formatted.Number value={mission.progress.targetValue} suffix={` ${mission.progress.unit}`} />
          </div>
        </div>
      )}
    </li>
  );
};

interface MissionButtonProps {
  logger?: () => void;
  json: {
    text: string;
    web: string;
    app?: string;
  };
}

const MissionButton = ({ logger, json }: MissionButtonProps) => {
  const [, copyToClipboard] = useCopyToClipboard();
  const { referralCode } = useReferralCode();

  if (json.web === '?action=referral_code') {
    return (
      <Button
        hierarchy='primary'
        color='neutral'
        size='small'
        label={json.text}
        onClick={() => {
          const url = new URL(convertWebviewToWebUrl(window.location.pathname));
          if (referralCode) {
            url.searchParams.set('referral_code', referralCode);
            url.searchParams.set('referral_type', 'copy');
          }
          copyToClipboard(url.toString());
          snackbar.success({
            message: referralCode ? (
              <FormattedMessage id='Competition.r251215.Link_and_code_copied_successfully' />
            ) : (
              <FormattedMessage id='Competition.r251215.Link_copied_successfully' />
            ),
          });
        }}
        disabled={!referralCode}
        css={{ minW: 'fit-content' }}
      />
    );
  }

  return (
    <CustomLink
      hierarchy='primary'
      color='neutral'
      size='small'
      label={json.text}
      href={json.web}
      locator={json.app}
      logger={logger}
      css={{ minW: 'fit-content' }}
    />
  );
};

const AnimatedProgressBar = ({
  progress,
  disabled = false,
}: {
  progress: { currentValue: number; targetValue: number };
  disabled?: boolean;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const percentage = (progress.currentValue / progress.targetValue) * 100;

  return (
    <div
      className={css({
        w: '100%',
        h: 8,
        bgColor: 'background.default.level-3',
        rounded: 999,
        overflow: 'hidden',
      })}
      ref={ref}
    >
      <m.div
        className={css(
          {
            h: '100%',
            rounded: 999,
            bgColor: 'content.default.level-3',
          },
          disabled && { bgColor: 'content.interaction.disabled' },
        )}
        initial={{ width: disabled ? `${percentage}%` : '0%' }}
        animate={{ width: disabled ? `${percentage}%` : isInView ? `${percentage}%` : '0%' }}
        transition={{
          duration: disabled ? 0 : 1.5,
          ease: [0.4, 0, 0.2, 1],
        }}
      />
    </div>
  );
};
